import React from "react";
import { StagePosition } from "./offeringCatalog";

/**
 * Presentation tokens for the theme-aware prototype match (ADO 9290).
 *
 * Accent hues stay constant across light and dark surfaces. Where an accent
 * would fail contrast as text on a light surface, the light-mode value is a
 * slightly darker shade of the same hue and the true accent is restored via a
 * `dark:` variant. All class strings are literal so Tailwind's JIT can compile
 * the arbitrary values; do not build these dynamically.
 */

/** "+" new marker: faint accent tint, accent border, accent text. */
export const stageMarkerClasses: Record<StagePosition, string> = {
  1: "bg-[#34D399]/15 border-[#34D399]/50 text-[#0e9f6e] dark:text-[#34D399]",
  2: "bg-[#60A5FA]/15 border-[#60A5FA]/50 text-[#2563EB] dark:text-[#60A5FA]",
  3: "bg-[#A78BFA]/15 border-[#A78BFA]/50 text-[#7C3AED] dark:text-[#A78BFA]",
  4: "bg-[#FBBF24]/20 border-[#FBBF24]/60 text-[#B45309] dark:text-[#FBBF24]",
};

/** Solid accent chip used for the stage number circle. */
export const stageSolidBgClasses: Record<StagePosition, string> = {
  1: "bg-[#34D399]",
  2: "bg-[#60A5FA]",
  3: "bg-[#A78BFA]",
  4: "bg-[#FBBF24]",
};

/** Accent text (persona label, cumulative total), darkened for light surfaces. */
export const stageAccentTextClasses: Record<StagePosition, string> = {
  1: "text-[#0e9f6e] dark:text-[#34D399]",
  2: "text-[#2563EB] dark:text-[#60A5FA]",
  3: "text-[#7C3AED] dark:text-[#A78BFA]",
  4: "text-[#B45309] dark:text-[#FBBF24]",
};

type TagHue = "gold" | "indigo" | "rose";

const TAG_HUE: Record<string, TagHue> = {
  INSPECT: "gold",
  FINANCIALS: "gold",
  REPORTS: "gold",
  INVENTORY: "gold",
  "RUN CALL": "indigo",
  DISPATCH: "indigo",
  "CALL INTAKE": "rose",
  MARKETING: "rose",
};

const TAG_HUE_CLASSES: Record<TagHue, string> = {
  gold: "bg-[#d98a2b]/15 border border-[#d98a2b]/40 text-[#a5661a] dark:text-[#d98a2b]",
  indigo:
    "bg-[#5b6bd6]/15 border border-[#5b6bd6]/40 text-[#4550b8] dark:text-[#8791e0]",
  rose: "bg-[#c2415a]/15 border border-[#c2415a]/40 text-[#a8324a] dark:text-[#e07a8f]",
};

/** Product-line tag chip classes for a given tag label. */
export function tagChipClasses(tag: string): string {
  return TAG_HUE_CLASSES[TAG_HUE[tag] ?? "gold"];
}

/** Inline gradient bar for a category header, keyed off its accent hex. */
export function categoryGradientStyle(hex: string): React.CSSProperties {
  return {
    backgroundImage: `linear-gradient(90deg, ${hex} 12%, transparent 70%)`,
  };
}

/** Subtle blue wash marking the member's current stage column, both themes. */
export const CURRENT_COLUMN_TINT =
  "bg-[#2f5fc0]/[0.05] dark:bg-[#2f5fc0]/[0.15]";

/** "YOU ARE HERE" pill: solid blue, white text. */
export const YOU_ARE_HERE_PILL = "bg-[#2f5fc0] text-white";

/** A la carte accent text (orange), darkened for light surfaces. */
export const ALC_ACCENT_TEXT = "text-[#EA580C] dark:text-[#FF6B1A]";

/** A la carte availability dot: orange with a soft glow. */
export const ALC_DOT = "bg-[#FF6B1A] shadow-[0_0_8px_rgba(255,107,26,0.5)]";
